/**
 * Router Configuration
 * 
 * Centralized route definitions, metadata, and navigation structure
 */

import { LEGACY_PASSWORD_RECOVERY_PATH, RESET_PASSWORD_PATH } from '../auth/recoveryUrls'

/**
 * Route paths - single source of truth for app routes
 */
export const ROUTE_PATHS = Object.freeze({
  home: '/',
  workers: '/workers',
  time_admin: '/time-admin',
  calendar: '/calendar',
  announcements: '/announcements',
  admin_ops: '/admin-ops'
})

/**
 * Legacy route redirects - for backward compatibility
 */
export const LEGACY_ROUTE_REDIRECTS = Object.freeze({
  '/workforce-dashboard': ROUTE_PATHS.workers,
  [LEGACY_PASSWORD_RECOVERY_PATH]: RESET_PASSWORD_PATH
})

/**
 * Map route paths to view keys
 */
export const PATH_TO_VIEW = Object.freeze({
  [ROUTE_PATHS.home]: 'home',
  [ROUTE_PATHS.workers]: 'workers',
  [ROUTE_PATHS.time_admin]: 'time_admin',
  [ROUTE_PATHS.calendar]: 'calendar',
  [ROUTE_PATHS.announcements]: 'announcements',
  [ROUTE_PATHS.admin_ops]: 'admin_ops'
})

/**
 * Admin ops path prefix
 */
export const ADMIN_OPS_PREFIX = `${ROUTE_PATHS.admin_ops}/`

/**
 * Metadata for each view - titles, descriptions, etc.
 */
export const VIEW_META = Object.freeze({
  home: {
    title: 'Operations Home',
    description: 'Overview of workforce controls, time workflows, and announcements.'
  },
  workers: {
    title: 'Workers Workspace',
    description: 'Create and manage worker records with consistent GG design patterns.'
  },
  time_admin: {
    title: 'Time Administration',
    description: 'Review and correct time entries with auditable manager workflows.'
  },
  calendar: {
    title: 'Calendar',
    description: 'Review scheduled jobs, assignments, and date-based operations.'
  },
  announcements: {
    title: 'Announcements',
    description: 'Publish and manage communication updates for teams and sites.'
  },
  admin_ops: {
    title: 'Admin Operations',
    description: 'Unified access to workforce, issues, corrections, and reporting panels.'
  }
})

/**
 * Navigation item structure template
 */
export const NAVIGATION_ITEMS_TEMPLATE = [
  { key: 'home', label: 'Home' },
  { key: 'workers', label: 'Workers' },
  { key: 'time_admin', label: 'Time Admin' },
  { key: 'calendar', label: 'Calendar' },
  { key: 'announcements', label: 'Announcements' }
]

/**
 * Access roles for role switcher (dev/test only)
 */
export const ACCESS_ROLE_OPTIONS = Object.freeze([
  'new_hire',
  'employee',
  'shift_lead',
  'manager',
  'owner_manager',
  'operations_support',
  'admin'
])

/**
 * Feature flag: enable role switcher in test environments
 */
export const ENABLE_ROLE_SWITCHER = process.env.NODE_ENV === 'test' || process.env.REACT_APP_ENABLE_ROLE_SWITCHER === 'true'

/**
 * Reset password path for error context
 */
export { RESET_PASSWORD_PATH }
